import axios from "axios";
import { getCookie } from "@util/cookie";
import interview from '@service/interview';
import { catchErrorLog } from "@util/sentry";
import { DemoInterviewFormRequest, PracticeInterviewResponseItem } from "@type/interview";
import { PracticeInterviewInsights } from '@type/interview';
import { get } from "lodash";

const apiURL = import.meta.env.VITE_TALENT_AI_BE_BASE_URL;

// Request Types
export interface VideoCreateRequest {
  conversation_id: string;
  interview?: string;
  browser_details: string;
  assessment_id?: string;
  job_id?: string | null;
  email?: string;
}

export interface UpdateInterviewStatusRequest {
  conversation_id?: string;
  new_status: string;
  retake_reason?: string;
  user_id?: string;
}

export interface SendInterviewFeedbackRequest {
  user_id: string;
  conversation_id: string;
  interview_duration: string;
  assessment_id?: string;
  feedback_data: Record<string, any>;
}

export interface StartupSubmissionRequest {
  email: string;
  startup_name: string;
  hiring_investor_id: string;
}

// Response Types
export interface VideoResponse {
  id: string;
  user_id: string;
  conversation_id: string;
  s3_link: string | null;
  /**
   * Streaming recording URL (BE #3092). Prefer this over `s3_link` for the
   * player — it's range-capable / HLS so playback starts immediately and seeks
   * without a full download. Falls back to `s3_link` while the BE rolls out.
   */
  video_url?: string | null;
  /**
   * True when this recording came from the ElevenLabs (v2) engine. Its locally
   * recorded webm can carry a wrong/absent native duration, so when this is true
   * the duration must be read from `duration_video` below, not the <video> element.
   */
  is_video_el?: boolean;
  /** Authoritative interview duration in SECONDS (used when `is_video_el` is true). */
  duration_video?: number;
  /**
   * True when the BE has muxed the candidate video + EL conversation audio into ONE
   * seekable file. Then `s3_link` is that combined file (sound baked in) and
   * `conversation_audio_url` / `audio_offset_ms` are null. Play it as a single normal
   * `<video>` WITH sound — no muting, no separate `<audio>`, no dual-track sync.
   */
  is_combined?: boolean;
  /**
   * Full interview audio (AI + candidate, both voices, presigned mp3) — the
   * authoritative soundtrack for a v2 EL interview, since the `s3_link` video is
   * mic-only / effectively silent. May be `null` if not ready yet (post-call
   * poller still running), for v1, or once `is_combined` is true. Play this as the
   * sound with the video muted (transitional path before the combined file).
   */
  conversation_audio_url?: string | null;
  /** Milliseconds to offset the audio vs the video on playback (currently 0). */
  audio_offset_ms?: number;
  interview: string;
  created_at: string;
  updated_at: string;
}

export interface InterviewInsightsResponse {
  id: string;
  user_id: string;
  conversation_id: string;
  interview: string;
  insights: string | null;
  created_at: string;
  updated_at: string;
}

export interface StandardResponse<T> {
  code: number;
  message: string;
  data?: T | null;
  errors?: string[];
  meta?: Record<string, any>;
}

export interface InsightItem {
  title: string;
  description: string;
}

export interface Insight {
  type: "positive" | "suggestion" | string;
  content: string;
}

/** Classic shape: technical quality breakdowns + per-insight type/content list. */
export interface ClassicSegmentInsights {
  videoQuality: InsightItem[];
  audioQuality: InsightItem[];
  insights: Insight[];
}

export interface ClassicGlobalInsights {
  globalVideoQuality: InsightItem[];
  globalAudioQuality: InsightItem[];
  globalInsights: Insight[];
}

export interface VideoSegmentInsight {
  segment_index: number;
  segment_context: string;
  insights: ClassicSegmentInsights | PracticeInterviewInsights;
}

export interface CandidateInsightsResponse {
  status: string;
  video_insights: VideoSegmentInsight[];
  global_video_insights: ClassicGlobalInsights | PracticeInterviewInsights;
}

export function isPracticeInterviewInsights(
  g: ClassicGlobalInsights | PracticeInterviewInsights | null | undefined
): g is PracticeInterviewInsights {
  return !!g && Array.isArray((g as PracticeInterviewInsights).feedbacks);
}

export function isClassicGlobalInsights(
  g: ClassicGlobalInsights | PracticeInterviewInsights | null | undefined
): g is ClassicGlobalInsights {
  return !!g && Array.isArray((g as ClassicGlobalInsights).globalInsights);
}

export interface StartupSubmissionResponse {
  id: string;
  startup_name: string;
  email: string;
  hiring_investor_id: string;
  interview_submission_id: string | null;
  created_at: string;
  updated_at: string;
  token: {
    access_token: string;
    token_type: string;
  }
}

// New interface definitions
export interface InterviewSegment {
  duration: number;
  context: string;
}

export interface GenerateCandidateInsightsRequest {
  conversation_id: string;
  interview_segments: InterviewSegment[];
}

/**
 * Create a new interview record
 * @param payload The video creation data with conversation ID and optional interview name
 * @returns Promise resolving to the API response
 */
export const createInterview = async (
  payload: VideoCreateRequest,
  customApiURL?: string
): Promise<StandardResponse<any>> => {
  try {
    const token = getCookie("authToken") || getCookie("tempAuthToken");

    // if (!token) {
    //   throw new Error("Authentication token not found");
    // }
    const apiURLToUse = customApiURL || apiURL;
    console.log("Using API URL:", apiURLToUse);

    const response = await axios.post<StandardResponse<any>>(
      `${apiURLToUse}/interviews/create`,
      payload,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error: any) {
    console.error("Error creating interview:", error);
    return {
      code: error.response?.status || 500,
      message: error.response?.data?.message || "Error creating interview",
      errors: error.response?.data?.errors || [
        "Failed to create interview record",
      ],
    };
  }
};

/**
 * The `conversation_id` returned by POST /interviews/create is the single source
 * of truth (#3460) — on `201` AND `202` alike. The BE may hand back an id that
 * differs from the one we generated (an abandoned earlier attempt it can't adopt,
 * a retake, a second tab), and every later call in the session is keyed on it:
 * /interviews/el/session, /interviews/el/el-conversation,
 * /interviews/el/recording-complete, /interviews/v2/update-status, and the S3
 * upload key. Keeping the local id there silently loses the whole interview —
 * those endpoints 404 and nothing is persisted.
 *
 * Falls back to the locally generated id only when the response carries none.
 */
export const resolveConversationId = (
  response: StandardResponse<any> | undefined | null,
  localConversationId: string
): string => response?.data?.conversation_id || localConversationId;

/**
 * A 4xx from a completion call means the record isn't there (or the request is
 * wrong) — retrying cannot fix it, it only hides the real error and delays the
 * candidate (#3460). Retries are for network errors and 5xx.
 */
export const isClientError = (error: unknown): boolean => {
  const status = (error as { response?: { status?: number } })?.response?.status;
  return typeof status === "number" && status >= 400 && status < 500;
};

/**
 * Get video data by conversation ID or interview name and user ID
 * @param conversation_id The conversation ID to fetch video data for
 * @param interview The interview name
 * @param user_id The user ID
 * @returns Promise resolving to the video data
 */
export const getVideo = async (
  conversation_id?: string,
  interview?: string,
  user_id?: string
): Promise<VideoResponse> => {
  try {
    const token = getCookie("authToken") || getCookie("tempAuthToken");

    if (!token) {
      throw new Error("Authentication token not found");
    }

    // Check that at least conversation_id or (interview and user_id) are provided
    if (!conversation_id && !(interview && user_id)) {
      throw new Error(
        "Either conversation_id or both interview and user_id must be provided"
      );
    }

    let url = `${apiURL}/interviews/video?`;

    // Build query parameters based on what's provided
    const params = new URLSearchParams();

    if (conversation_id) {
      params.append("conversation_id", conversation_id);
    }

    if (interview) {
      params.append("interview", interview);
    }

    if (user_id) {
      params.append("user_id", user_id);
    }

    url += params.toString();

    const response = await axios.get<StandardResponse<VideoResponse>>(url, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data.data as VideoResponse;
  } catch (error: any) {
    console.error("Error fetching video data:", error);
    return {} as VideoResponse; // Return an empty object or handle error as needed
  }
};

/**
 * Get interview insights data by conversation ID or interview name and user ID
 * @param conversation_id The conversation ID to fetch interview insights for
 * @param interview The interview name
 * @param user_id The user ID
 * @returns Promise resolving to the interview insights
 */
export const getInterviewInsights = async (
  conversation_id?: string,
  interview?: string,
  user_id?: string
): Promise<InterviewInsightsResponse> => {
  try {
    const token = getCookie("authToken");

    if (!token) {
      throw new Error("Authentication token not found");
    }

    // Check that at least conversation_id or (interview and user_id) are provided
    if (!conversation_id && !(interview && user_id)) {
      throw new Error(
        "Either conversation_id or both interview and user_id must be provided"
      );
    }

    let url = `${apiURL}/interviews/interview_insights?`;

    // Build query parameters based on what's provided
    const params = new URLSearchParams();

    if (conversation_id) {
      params.append("conversation_id", conversation_id);
    }

    if (interview) {
      params.append("interview", interview);
    }

    if (user_id) {
      params.append("user_id", user_id);
    }

    url += params.toString();

    const response = await axios.get<
      StandardResponse<InterviewInsightsResponse>
    >(url, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data.data as InterviewInsightsResponse;
  } catch (error: any) {
    console.error("Error fetching interview insights:", error);
    return {} as InterviewInsightsResponse; // Return an empty object or handle error as needed
  }
};

/**
 * Update the status of an interview
 * @param payload The status update data containing new status, and optional retake reason
 * @returns Promise resolving to the API response
 */
export const updateInterviewStatus = async (
  payload: UpdateInterviewStatusRequest,
  customApiURL?: string
): Promise<StandardResponse<any>> => {
  try {
    const token = getCookie("authToken") || getCookie("tempAuthToken");
    const apiURLToUse = customApiURL || apiURL;
    console.log("Using API URL for update:", apiURLToUse);

    const url = `${apiURLToUse}/interviews/update-status`;
    console.log("Updating interview status with payload:", payload);
    const response = await axios.post<StandardResponse<any>>(
      url,
      payload,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error: any) {
    console.error("Error updating interview status:", error);
    return {
      code: error.response?.status || 500,
      message:
        error.response?.data?.message || "Error updating interview status",
      errors: error.response?.data?.errors || [
        "Failed to update interview status",
      ],
    };
  }
};

/**
 * Get the status of an interview
 * @returns Promise resolving to the API response
 */
export const getInterviewStatus = async (assessment_id?: string | null): Promise<StandardResponse<any>> => {
  // if assessment_id is None/Null, this will return the status of the screening interview
  // otherwise, it will return the status of the assessment interview
  try {
    const token = getCookie("authToken") || getCookie("tempAuthToken");

    if (!token) {
      throw new Error("Authentication token not found");
    }

    const endpoint = assessment_id ? `/interviews/${assessment_id}/user/status` : '/interviews/status';

    const response = await axios.get<StandardResponse<any>>(
      `${apiURL}${endpoint}`,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error: any) {
    console.error("Error updating interview status:", error);
    return {
      code: error.response?.status || 500,
      message:
        error.response?.data?.message || "Error updating interview status",
      errors: error.response?.data?.errors || [
        "Failed to update interview status",
      ],
    };
  }
};

/**
 * Get the status of an interview
 * @returns Promise resolving to the API response
 */
export const getCandidateInterviewStatus = async (
  user_id: string
): Promise<StandardResponse<any>> => {
  try {
    const token = getCookie("authToken");

    if (!token) {
      throw new Error("Authentication token not found");
    }

    const response = await axios.get<StandardResponse<any>>(
      `${apiURL}/interviews/${user_id}/status`,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error: any) {
    console.error("Error updating interview status:", error);
    return {
      code: error.response?.status || 500,
      message:
        error.response?.data?.message || "Error updating interview status",
      errors: error.response?.data?.errors || [
        "Failed to update interview status",
      ],
    };
  }
};

/**
 * Send internal interview complete notification
 * @param payload The notification data containing user ID, interview duration, and feedback data
 * @returns Promise resolving to the API response
 */
export const sendInternalInterviewCompleteNotification = async (
  payload: SendInterviewFeedbackRequest
): Promise<StandardResponse<any>> => {
  try {
    const token = getCookie("authToken") || getCookie("tempAuthToken");

    if (!token) {
      throw new Error("Authentication token not found");
    }

    const response = await axios.post<StandardResponse<any>>(
      `${apiURL}/interviews/send-interview-feedback-email`,
      payload,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error: any) {
    console.error("Error sending interview feedback notification:", error);
    return {
      code: error.response?.status || 500,
      message:
        error.response?.data?.message ||
        "Error sending interview feedback notification",
      errors: error.response?.data?.errors || [
        "Failed to send interview feedback notification",
      ],
    };
  }
};

export const triggerInterviewDoneEmail = async (): Promise<any> => {
  const token = getCookie("authToken");
  return await axios.post(
    `${apiURL}/interviews/trigger-interview-done-email`,
    {},
    {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );
};

/**
 * Get candidate insights by conversation ID
 * @param conversation_id The conversation ID to fetch insights for
 * @returns Promise resolving to the candidate insights data
 */
export const getCandidateInsights = async (
  conversation_id: string
): Promise<CandidateInsightsResponse | null> => {
  try {
    const token = getCookie("authToken");

    if (!token) {
      throw new Error("Authentication token not found");
    }

    const url = `${apiURL}/pipecat/candidate_insights/${conversation_id}`;

    const response = await axios.get<StandardResponse<CandidateInsightsResponse>>(url, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data?.data ?? null;
  } catch (error: any) {
    console.error("Error fetching candidate insights:", error);
    return null; // Return null on error
  }
};

/**
 * Generate candidate insights from interview segments (for non-practice interviews)
 * @param payload Request payload containing conversation_id and interview segments
 * @returns Promise resolving to the response data
 */
export const generateCandidateInsights = async (
  payload: GenerateCandidateInsightsRequest
): Promise<any> => {
  try {
    const token = getCookie("authToken") || getCookie("tempAuthToken");
    if (!token) {
      throw new Error("Authentication token not found");
    }

    const url = `${apiURL}/pipecat/candidate_insights`;

    const response = await axios.post(url, payload, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data;
  } catch (error: any) {
    console.error("Error generating candidate insights:", error);
    return null; // Return null on error
  }
};

/**
 * Generate candidate insights from interview segments (for practice interviews)
 * @param payload Request payload containing conversation_id and interview segments
 * @returns Promise resolving to the response data
 */
export const generatePracticeInsights = async (
  payload: GenerateCandidateInsightsRequest
): Promise<any> => {
  try {
    const token = getCookie("authToken");

    if (!token) {
      throw new Error("Authentication token not found");
    }

    const url = `${apiURL}/pipecat/practice_insights`;

    const response = await axios.post(url, payload, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data;
  } catch (error: any) {
    console.error("Error generating candidate insights:", error);
    return null; // Return null on error
  }
};


/**
 * Get practice interviews
 * @param skip Number of items to skip for pagination
 * @param limit Maximum number of items to return
 * @returns Promise resolving to the API response with practice interviews
 */
export const getPracticeInterviews = async (
  page: number = 1,
  limit: number = 100
): Promise<StandardResponse<{
  interviews: PracticeInterviewResponseItem[];
  total: number;
  page: number;
  limit: number;
}>> => {
  try {
    const response = await axios.get<StandardResponse<any>>(
      `${apiURL}/interviews/list-practice-interviews?page=${page}&limit=${limit}`,
    );

    return response.data;
  } catch (error) {
    catchErrorLog(`failed to get practice interviews, err: ${error}`);
    return {
      code: 500,
      message: "Failed to fetch practice interviews",
      data: null,
      errors: error instanceof Error ? [error.message] : ["Unknown error"],
    };
  }
};

export interface CompletedPracticeInterviewDetail {
  id: string;
  hr_id: string;
  type: string;
  content: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  status: "Complete" | "Pending Processing" | string;
  is_default: boolean;
  // Final score (0–100). Top-level on `data`, not nested under
  // `content`. May be absent while status === "Pending Processing".
  score?: number;
}

export const getCompletedPracticeInterview = async (
  assessment_id: string
): Promise<StandardResponse<CompletedPracticeInterviewDetail>> => {
  try {
    const token = getCookie("authToken");

    if (!token) {
      throw new Error("Authentication token not found");
    }

    const response = await axios.get<StandardResponse<CompletedPracticeInterviewDetail>>(
      `${apiURL}/interviews/completed-practice-interviews/${assessment_id}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return response.data;
  } catch (error) {
    catchErrorLog(`failed to get completed practice interview ${assessment_id}, err: ${error}`);
    return {
      code: 500,
      message: "Failed to fetch completed practice interview",
      data: null,
      errors: error instanceof Error ? [error.message] : ["Unknown error"],
    };
  }
};

export const getCompletedPracticeInterviews = async (
  page: number = 1,
  limit: number = 100
): Promise<StandardResponse<{
  interviews: PracticeInterviewResponseItem[];
  total: number;
  skip: number;
  limit: number;
}>> => {
  try {
    const token = getCookie("authToken");

    if (!token) {
      throw new Error("Authentication token not found");
    }

    const response = await axios.get<StandardResponse<any>>(
      `${apiURL}/interviews/list-completed-practice-interviews?page=${page}&limit=${limit}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return response.data;
  } catch (error) {
    catchErrorLog(`failed to get completed practice interviews, err: ${error}`);
    return {
      code: 500,
      message: "Failed to fetch completed practice interviews",
      data: null,
      errors: error instanceof Error ? [error.message] : ["Unknown error"],
    };
  }
};

export const getOurteamInterviews = async (): Promise<any> => {
  const token = getCookie("authToken");
  return await axios.get(
    `${apiURL}/interviews/list-ourteam-interviews`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
};

export const getLeaderboardInterviews = async (assessment_id: string, my_score?: boolean): Promise<any> => {
  const token = getCookie("authToken");
  return await axios.get(
    `${apiURL}/interviews/leaderboard`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: {
        assessment_id: assessment_id,
        my_score: my_score,
      }
    }
  );
};



/**
 * Create a startup submission for the first move interview
 * @param payload The startup submission data containing email, startup name, and hiring investor ID
 * @returns Promise resolving to the API response
 */
export const createStartupSubmission = async (
  payload: StartupSubmissionRequest
): Promise<StandardResponse<StartupSubmissionResponse>> => {
  try {
    const token = getCookie("authToken");

    // For this endpoint, we might not require authentication
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    // Add token if available
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const response = await axios.post<StandardResponse<StartupSubmissionResponse>>(
      `${apiURL}/interviews/create-startup-submission`,
      payload,
      { headers }
    );
    return {
      code: response.data.code,
      message: response.data.message,
      data: response.data.data || null,
    };
  } catch (error: any) {
    console.error("Error creating startup submission:", error);
    return {
      code: error.response?.status || 500,
      message: error.response?.data?.message || "Please use your company email address to continue.",
      errors: error.response?.data?.errors || ["Failed to create startup submission"],
      data: error.response?.data?.data || null,
    };
  }
};

export const updateInterviewStatusV2 = async (
  payload: UpdateInterviewStatusRequest
): Promise<any> => {
  const token = getCookie("authToken") || getCookie("tempAuthToken");
  return await axios({
    url: `${apiURL}/interviews/v2/update-status`,
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    data: payload,
  });
};

export const getInterviewStatusV2 = async (assessment_id?: string | null): Promise<StandardResponse<any>> => {
  // if assessment_id is None/Null, this will return the status of the screening interview
  // otherwise, it will return the status of the assessment interview
  try {
    const token = getCookie("authToken") || getCookie("tempAuthToken");

    if (!token) {
      throw new Error("Authentication token not found");
    }

    const endpoint = assessment_id ? `/interviews/${assessment_id}/user/status` : '/interviews/status-v2';

    const response = await axios.get<StandardResponse<any>>(
      `${apiURL}${endpoint}`,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error: any) {
    console.error("Error updating interview status:", error);
    return {
      code: error.response?.status || 500,
      message:
        error.response?.data?.message || "Error updating interview status",
      errors: error.response?.data?.errors || [
        "Failed to update interview status",
      ],
    };
  }
};

export const getAssessmentFormStatus = async (assessment_id: string, job_id: string): Promise<any> => {
  const token = getCookie("authToken") || getCookie("tempAuthToken");
  return await axios({
    url: `${apiURL}/interviews/${assessment_id}/job/${job_id}/form/status`,
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
};

export async function createInterviewContext(companyId: string, context: string): Promise<any> {
  const token = getCookie("authToken");
  return await axios({
    url: `${apiURL}/interview-contexts`,
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    data: {
      company_id: companyId,
      context,
    },
  });
}

export async function getInterviewContexts(companyId: string): Promise<any> {
  const token = getCookie("authToken");
  return await axios({
    url: `${apiURL}/interview-contexts/${companyId}`,
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
}

export const triggerOutboundCall = (recipientNumber: string, name: string, email: string) => {
  return axios.post(
    `${apiURL}/interviews/create-outbound-call`,
    { recipient_number: recipientNumber, name, email },
    {
      headers: {
        "Content-Type": "application/json",
      },
    }
  );
};

export default {
  createInterview,
  getVideo,
  getInterviewInsights,
  updateInterviewStatus,
  sendInternalInterviewCompleteNotification,
  getCandidateInsights,
  generateCandidateInsights,
  getPracticeInterviews,
  createStartupSubmission,
};